
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { LineItemFormValues, lineItemFormSchema, defaultFormValues } from './useLineItemFormSchema';
import { useLineItemData, CategoryOrTrade } from './useLineItemData';
import { useLineItemSubmit } from './useLineItemSubmit';
import { useUserProfile } from './useUserProfile';
import { PricingTier } from '../types';
import useMarkupCalculation from '@/hooks/useMarkupCalculation';
import { LineItemMarkupTier } from '@/types/line-item-markup-tiers';

interface UseLineItemFormProps {
  lineItemId?: string;
}

export const useLineItemForm = ({ lineItemId }: UseLineItemFormProps) => {
  // Form state
  const [enableConversion, setEnableConversion] = useState(false);
  const [enableProductivity, setEnableProductivity] = useState(false);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);
  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<CategoryOrTrade | null>(null);
  const [selectedTrade, setSelectedTrade] = useState<CategoryOrTrade | null>(null);
  const [selectedBrowseCategory, setSelectedBrowseCategory] = useState<string | null>(null);

  // Get user profile for company ID
  const profile = useUserProfile();
  const companyId = profile?.company_id;

  // Initialize form with Zod resolver
  const form = useForm<LineItemFormValues>({
    resolver: zodResolver(lineItemFormSchema),
    defaultValues: defaultFormValues,
  });

  // Watch form values for real-time calculations
  const baseCost = form.watch('base_cost') || 0;
  const customTiers = form.watch('custom_tiers') || [];
  const useCustomTiers = form.watch('use_custom_tiers') || false;
  const overrideMarkup = form.watch('override_markup') || false;
  const customMarkup = form.watch('custom_markup') || 0;

  // Convert form tiers to LineItemMarkupTier format, filtering incomplete ones
  const validCustomTiers: LineItemMarkupTier[] = customTiers.filter(tier => 
    tier.min_amount !== undefined && 
    tier.markup_percentage !== undefined && 
    tier.margin_percentage !== undefined
  ).map(tier => ({
    id: tier.id || `temp-${Date.now()}`,
    min_amount: tier.min_amount!,
    max_amount: tier.max_amount || null,
    markup_percentage: tier.markup_percentage!,
    margin_percentage: tier.margin_percentage!,
    line_item_id: tier.line_item_id || undefined
  }));

  // Real-time markup calculation
  const markupCalculation = useMarkupCalculation({
    amount: baseCost,
    categoryId: selectedCategory?.id,
    companyId: companyId || '',
    customTiers: validCustomTiers,
    useCustomTiers: useCustomTiers
  });

  // Load data if editing
  const { isLoading, isEditMode } = useLineItemData({
    lineItemId,
    form,
    setEnableConversion,
    setEnableProductivity,
    setImagePreviewUrls,
    setPricingTiers,
    setSelectedCategory,
    setSelectedTrade,
    setSelectedBrowseCategory
  });

  // Submit handler
  const { onSubmit, handleCancel, isLoading: isSubmitting } = useLineItemSubmit({
    lineItemId,
    selectedCategory,
    selectedTrade,
    selectedBrowseCategory,
    pricingTiers,
    imagePreviewUrls
  });

  // Calculate effective markup and final price
  const effectiveMarkup = overrideMarkup ? customMarkup : (markupCalculation.result?.markup || 0);
  const effectiveMargin = markupCalculation.result?.margin || 0;
  const finalPrice = overrideMarkup 
    ? baseCost * (1 + customMarkup / 100)
    : (markupCalculation.result?.finalPrice || baseCost);

  // Handler for markup override toggle
  const handleOverrideToggle = (checked: boolean) => {
    form.setValue('override_markup', checked);
    if (!checked) {
      // Reset custom markup when disabling override
      form.setValue('custom_markup', 0);
    }
  };

  // Handlers for custom markup changes
  const handleCustomMarkupChange = (value: number) => {
    form.setValue('custom_markup', value);
  };

  const handleCustomMarginChange = (value: number) => {
    // Calculate markup from margin: markup = margin / (100 - margin) * 100
    const calculatedMarkup = value > 0 ? (value / (100 - value)) * 100 : 0;
    form.setValue('custom_markup', calculatedMarkup);
  };

  const handlePricingRefreshed = () => {
    // Trigger a re-calculation by updating a timestamp or similar
    console.log('Pricing refreshed');
  };

  return {
    form,
    isLoading: isLoading || isSubmitting || markupCalculation.loading,
    isEditMode,
    pricingTiers,
    setPricingTiers,
    enableConversion,
    setEnableConversion,
    enableProductivity,
    setEnableProductivity,
    imageFiles,
    setImageFiles,
    imagePreviewUrls,
    setImagePreviewUrls,
    selectedCategory,
    setSelectedCategory,
    selectedTrade,
    setSelectedTrade,
    selectedBrowseCategory,
    setSelectedBrowseCategory,
    onSubmit,
    handleCancel,
    // Markup calculation results
    companyId,
    baseCost,
    effectiveMarkup,
    effectiveMargin,
    finalPrice,
    overrideMarkup,
    validCustomTiers,
    useCustomTiers,
    markupResult: {
      sourceLabel: markupCalculation.result?.sourceLabel || 'Global Default',
      sourceName: markupCalculation.result?.sourceName || 'global'
    },
    // Markup handlers
    handleOverrideToggle,
    handleCustomMarkupChange,
    handleCustomMarginChange,
    handlePricingRefreshed
  };
};
